import { Badge } from '../../../atoms/Badge/Badge';
import { Button } from '../../../atoms/Button/Button';
import { Icon } from '../../../atoms/Icon/Icon';
import { PlatformIcon } from '../../../atoms/PlatformIcon/PlatformIcon';
import { platformFor } from '@deepdive-icons';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { TableHeader } from '../../../molecules/TableHeader/TableHeader';
import type { TableHeaderSort } from '../../../molecules/TableHeaderLabel/TableHeaderLabel';
import { Table, type TableColumn, type TableRowData } from '../../../organisms/Table/Table';
import { SENTIMENT_BADGE, pageWindow, wholeFormat as whole } from '../../tables/shared/tableShared';
import styles from './ConversationsPanel.module.css';

/**
 * A list of conversations, sorted and paged — the body every conversations
 * modal in the app puts on the overlay (prototype `ConversationsPanel`).
 *
 * The columns, the sort rules, the page size and the `View` are facts about a
 * conversation list, not about what selected it. Only the heading knows the
 * difference.
 *
 * **Sorted and paged by the server** in this port: the caller owns the
 * `ConversationsView` (it did in the prototype too, so a `Breakdown ›` round
 * trip keeps it) and fetches the page it names. `rows` is that one page.
 */

/** Ten rows, as the frame (`Modal` `Type=Table`, 2235:112497) draws. */
export const CONVERSATIONS_PAGE_SIZE = 10;

export type ConversationsPanelSortKey =
  | 'engagement'
  | 'channel'
  | 'sentiment'
  | 'topic'
  | 'reactions'
  | 'comments'
  | 'shares'
  | 'intent'
  | 'postedAt';

export type ConversationsSortState = { key: ConversationsPanelSortKey; direction: 'asc' | 'desc' };

/**
 * Where the reader has got to in the list — the sort and the page. Held by the
 * caller, so `‹` from a breakdown lands back on the same sorted page.
 */
export type ConversationsView = { sort: ConversationsSortState; page: number };

/** What a list shows when it is opened fresh: Engagement, highest first, page 1. */
export const DEFAULT_CONVERSATIONS_VIEW: ConversationsView = {
  sort: { key: 'engagement', direction: 'desc' },
  page: 1,
};

/* Words open A–Z and sentiment opens positive-first; every figure and the date open highest first. */
const opensAscending = (key: ConversationsPanelSortKey) =>
  key === 'topic' || key === 'intent' || key === 'sentiment' || key === 'channel';

/** One conversation, labelled for its cells. */
export type ConversationRowView = {
  id: number;
  text: string;
  channel: string;
  channelLabel: string;
  sentiment: string;
  sentimentLabel: string;
  intentLabel: string;
  topicLabel: string;
  /** Reactions **including likes**. */
  reactions: number;
  comments: number;
  shares: number;
  engagement: number;
  /** `Mon Sep 14, 9:42 PM`. */
  postedLabel: string;
};

export type ConversationsPanelProps = {
  /** The current page of conversations. */
  rows: ConversationRowView[];
  /** How many conversations the whole list holds — drives the pagination. */
  total: number;
  /** The `TableHeader` title, and what the dialog is labelled by. */
  title: string;
  titleId: string;
  /** The pill beside the title — "Showing 8,397 conversations for Neutral". */
  badge: string;
  helpText?: string;
  helpSupportingText?: string;
  /** The table's accessible name, which says what this list is of. */
  label: string;
  /** Overrides the empty line (a failed or unconnected load). */
  emptyLabel?: string;
  /** Figma's `View` on each conversation — the post itself. */
  onViewConversation?: (conversation: ConversationRowView) => void;
  /** Figma's `Breakdown ›` on each conversation. Omit and the button stays inert. */
  onBreakdown?: (conversation: ConversationRowView) => void;
  view: ConversationsView;
  onViewChange: (view: ConversationsView) => void;
};

export function ConversationsPanel({
  rows,
  total,
  title,
  titleId,
  badge,
  helpText,
  helpSupportingText,
  label,
  emptyLabel,
  onViewConversation,
  onBreakdown,
  view,
  onViewChange,
}: ConversationsPanelProps) {
  const { sort, page: pageNumber } = view;
  const pageCount = Math.max(1, Math.ceil(total / CONVERSATIONS_PAGE_SIZE));
  const current = Math.min(pageNumber, pageCount);
  const setPageNumber = (page: number) => onViewChange({ ...view, page });

  const sortFor = (key: ConversationsPanelSortKey): TableHeaderSort =>
    sort.key === key ? sort.direction : 'selector';
  /*
   * Sort and page move together, in **one** `onViewChange`: re-sorting sends
   * the reader back to page 1, and two calls off the same `view` would each
   * spread the stale other half, so the second would undo the first.
   */
  const onSort = (key: ConversationsPanelSortKey) => () =>
    onViewChange({
      page: 1,
      sort:
        sort.key === key
          ? { key, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
          : { key, direction: opensAscending(key) ? 'asc' : 'desc' },
    });
  const sortable = (
    key: ConversationsPanelSortKey,
    header: string,
    width: string | undefined,
    extra?: Partial<TableColumn>,
  ): TableColumn => ({
    key,
    header,
    width,
    sort: sortFor(key),
    onSort: onSort(key),
    ...extra,
  });

  /*
   * **Re-cut on 2026-09-25** to the frame's current cut (`Modal` `Type=Table`,
   * 2235:112497): `Channel` is back, second, at 88; `Page URL` 78 becomes
   * `Actions` 169, holding `View` and `Breakdown ›`. Both come out of
   * `Conversation`, 363 → 184.
   *
   * **Figma's widths for `Intent` 75, `Topic` 73 and `Date posted` 110 clipped**
   * — they were sized for the placeholder `12,975`, and the real values are
   * words ("Recommendation", "Network coverage"). Every column but
   * `Conversation` is `fit` now — as wide as its longest value (or its header),
   * never truncated. Figma's percentages for the rest were already at their
   * header's minimum, and left in they pushed the grid 9px past the card once
   * the three above grew. `Conversation` is the column left without a width, so
   * it takes what is left and keeps the ellipsis, with the full text on hover
   * (`reveal`). See plan.md §7.
   */
  const columns: TableColumn[] = [
    { key: 'conversation', header: 'Conversation', reveal: true },
    sortable('channel', 'Channel', undefined, { cellType: 'actionIcons', fit: true }),
    sortable('sentiment', 'Sentiment', undefined, { cellType: 'badge', fit: true }),
    sortable('intent', 'Intent', undefined, { fit: true }),
    sortable('topic', 'Topic', undefined, { fit: true }),
    sortable('reactions', 'Reactions', undefined, { fit: true }),
    sortable('comments', 'Comments', undefined, { fit: true }),
    sortable('shares', 'Shares', undefined, { fit: true }),
    sortable('engagement', 'Engagement', undefined, { fit: true }),
    sortable('postedAt', 'Date posted', undefined, { fit: true }),
    { key: 'actions', header: 'Actions', cellType: 'actionButtons', fit: true },
  ];

  const tableRows: TableRowData[] = rows.map((c) => ({
    key: String(c.id),
    cells: {
      /* Truncates at the column's edge, as the frame's does; the full post is its accessible text and the hover tooltip. */
      conversation: { text: c.text },
      /*
       * Figma wraps the mark in a 20x20 icon-only button; nothing acts on a
       * channel here, so this is the bare mark — labelled, since it is the
       * cell's only content.
       */
      channel: {
        /* The key on mock data, the name on live (where the key is an id). */
        content: (() => {
          const platform = platformFor(c.channel) ?? platformFor(c.channelLabel);
          return platform ? <PlatformIcon name={platform.name} label={c.channelLabel} /> : null;
        })(),
      },
      engagement: { text: whole.format(c.engagement) },
      sentiment: {
        content: <Badge type={SENTIMENT_BADGE[c.sentiment] ?? 'gray'}>{c.sentimentLabel}</Badge>,
      },
      topic: { text: c.topicLabel },
      reactions: { text: whole.format(c.reactions) },
      comments: { text: whole.format(c.comments) },
      shares: { text: whole.format(c.shares) },
      intent: { text: c.intentLabel },
      postedAt: { text: c.postedLabel },
      /*
       * Figma's two buttons, in its own order and hierarchies: `View` is
       * `Tertiary` with no icon, `Breakdown` is `Ghost Color` with a trailing
       * chevron. The frame's third button, `Delete`, is `visible=false`.
       */
      actions: {
        content: (
          <>
            <Button
              hierarchy="tertiary"
              onClick={() => onViewConversation?.(c)}
              aria-label={`View conversation: ${c.text}`}
            >
              View
            </Button>
            <Button
              hierarchy="ghostColor"
              iconTrailing={<Icon name="chevronRight" />}
              onClick={() => onBreakdown?.(c)}
              aria-label={`Breakdown of conversation: ${c.text}`}
            >
              Breakdown
            </Button>
          </>
        ),
      },
    },
  }));

  return (
    /*
     * `landing`: where the dialog puts focus when it opens. A native dialog
     * focuses the element carrying the `autofocus` *attribute*, else its first
     * focusable element — here the title's help icon, whose tooltip would then
     * open over the table on every open. The attribute is written by a ref
     * because React's `autoFocus` never writes it.
     */
    <div className={styles.panel} tabIndex={-1} ref={(el) => el?.setAttribute('autofocus', '')}>
      <Table
        elevated
        label={label}
        columns={columns}
        rows={tableRows}
        emptyLabel={emptyLabel ?? 'No conversations in this period'}
        header={
          <TableHeader
            title={title}
            titleId={titleId}
            badge={badge}
            helpText={helpText}
            helpSupportingText={helpSupportingText}
          />
        }
        pagination={
          <Pagination
            page={current}
            pageCount={pageCount}
            pages={pageWindow(current, pageCount)}
            onPageChange={setPageNumber}
            label="Conversation pages"
          />
        }
      />
    </div>
  );
}

/** Shared with the modals, so a count and its noun never disagree. */
export function conversationsBadge(count: number, forWhat: string): string {
  return `Showing ${whole.format(count)} ${count === 1 ? 'conversation' : 'conversations'} for ${forWhat}`;
}
